// src/oura/components/onboarding/FirstResultsBanner.tsx
//
// Staff, on a business's answers (Visibility) while its first AI check's
// results wait for approval (Jack, 20 Oct 2026): one line on top that says
// these are the first results, and the button that approves them. Approving
// starts Oura on the Baseline Snapshot. Nothing reaches the client until the
// results and plan are sent from Onboarding. After, it says so and links
// back to Onboarding, where the timeline and the send are.
//
// Reads the onboarding record itself and renders nothing when the record does
// not track the step (an older backend) or the results are not waiting. A read
// that fails says so, with Try again: hiding would take away the only approve.

import { useEffect, useState } from "react";
import { ArrowRightIcon, CheckIcon, ListChecksIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { PillButton, PillLink, SURFACE, TYPE } from "@oura/ui";
import { approveResults, getOnboarding, type Onboarding } from "@service/aeo/onboarding";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@component/dialog";
import { callName, dayMonth } from "../../onboarding/derive";
import { useHub } from "../../overview/useHubStage";
import { isEditRescanPill } from "../../overview/hubStage";

export function FirstResultsBanner({ businessId, onboardingHref }: { businessId: string; onboardingHref: string }) {
    const [o, setO] = useState<Onboarding | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [reload, setReload] = useState(0);
    const [busy, setBusy] = useState(false);
    const [approvalFailed, setApprovalFailed] = useState(false);
    // Approving has no undo here (only Edit and rescan, a new paid check), so it asks first (#4450).
    const [confirming, setConfirming] = useState(false);
    // Edit and rescan, from the business header's own stage pills: the same press, moved beside
    // Approve results while the first results wait (the header leaves it out here).
    const hub = useHub();
    const edit = hub?.pills.find(isEditRescanPill) ?? null;

    useEffect(() => {
        let live = true;
        setO(null);
        setLoadFailed(false);
        getOnboarding(businessId).then((r) => { if (live) setO(r ?? null); }).catch(() => { if (live) setLoadFailed(true); });
        return () => { live = false; };
    }, [businessId, reload]);

    if (loadFailed) {
        return (
            <section role="alert" aria-label="First AI check results" className={cn(SURFACE.card, "mb-5 flex flex-wrap items-center justify-between gap-3 p-5")}>
                <p className={cn(TYPE.tc, "m-0 text-rose-600")}>Could not read whether the first check's results wait for approval.</p>
                <PillButton onClick={() => setReload((n) => n + 1)} className="max-sm:h-9">Try again</PillButton>
            </section>
        );
    }

    // Only while the first check is done and its results are not sent: before, there is nothing to approve; after, it is history.
    if (!o || o.scan.status !== "complete" || o.resultsSentAt !== null || o.resultsApprovedAt === undefined) return null;
    const approved = Boolean(o.resultsApprovedAt);
    // Nothing reaches the client before Send, the shared report included (the header's Share report is shut too).
    const contact = callName(o.contact.name) || "the client";
    const shareNote = `Share report opens after you send the results to ${contact}.`;

    const approve = async () => {
        setBusy(true);
        setApprovalFailed(false);
        try {
            await approveResults(businessId);
            setO((await getOnboarding(businessId)) ?? o);
        } catch {
            setApprovalFailed(true);
        } finally {
            setBusy(false);
            setConfirming(false);
        }
    };

    return (
        <section aria-label="First AI check results" className={cn(SURFACE.card, "mb-5 flex flex-wrap items-center gap-x-5 gap-y-3 p-5")}>
            <span aria-hidden className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-full", approved ? "bg-emerald-500 text-white" : "bg-blue-50 text-blue-600")}>
                {approved ? <CheckIcon size={18} strokeWidth={3} /> : <ArrowRightIcon size={18} />}
            </span>
            <div className="min-w-0 flex-1 basis-64">
                <p className={cn(TYPE.tt, "m-0 text-gray-900")}>{approved ? "Results approved" : "First AI check results, ready to review"}</p>
                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                    {approved
                        ? `${o.snapshotReadyAt ? "The Baseline Snapshot is ready." : "Oura is making the Baseline Snapshot from them."} The timeline and the send are on Onboarding. ${shareNote}`
                        : `Finished ${dayMonth(o.scan.finishedAt)}. Read the answers below, then approve. Oura makes the Baseline Snapshot from what you approve. ${shareNote}`}
                </p>
                {approvalFailed && <p role="alert" className={cn(TYPE.tc, "m-0 mt-1 font-medium text-rose-600")}>That did not go through. Try again in a moment.</p>}
            </div>
            {/* Secondary left, the primary last on the right. */}
            <div className="flex flex-wrap items-center gap-2">
                {edit && hub && (
                    <PillButton icon={ListChecksIcon} disabled={hub.busy} writes onClick={() => hub.act(edit.act)} className="max-sm:h-9">
                        {edit.label}
                    </PillButton>
                )}
                {approved ? (
                    <PillLink to={onboardingHref} className="max-sm:h-9">Back to Onboarding</PillLink>
                ) : (
                    <PillButton variant="primary" busy={busy} writes onClick={() => setConfirming(true)} className="max-sm:h-9">
                        Approve results
                    </PillButton>
                )}
            </div>
            <Dialog open={confirming} onOpenChange={(open) => { if (!open && !busy) setConfirming(false); }}>
                <DialogContent className="gap-0 p-6 sm:max-w-[460px] sm:rounded-2xl">
                    <DialogTitle className="pr-8 text-lg font-semibold leading-snug text-gray-900">
                        Approve {o.businessName ? `${o.businessName}'s` : "the"} first AI check?
                    </DialogTitle>
                    <DialogDescription asChild>
                        <div className={cn(TYPE.tb, "mt-2 space-y-2 text-gray-600")}>
                            <p className="m-0">Oura makes the Baseline Snapshot from these answers.</p>
                            <p className="m-0 text-gray-500">To change them after this, you edit and rescan, which runs a new paid check. Nothing reaches {contact} until you send.</p>
                        </div>
                    </DialogDescription>
                    <div className="mt-5 flex justify-end gap-2">
                        <PillButton size="sm" disabled={busy} onClick={() => setConfirming(false)}>Cancel</PillButton>
                        <PillButton size="sm" variant="primary" busy={busy} writes onClick={() => void approve()}>
                            {busy ? "Approving…" : "Approve results"}
                        </PillButton>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
}
